import { useNavigate } from 'react-router-dom';
import React, { createRef, useCallback, useEffect, useState } from 'react';
import { sendEmailVerification, verifyEmail } from '../services/AuthService';
import CustomSnackbar from '../../components/CustomSnackbar';
import { useTranslations } from '../../localization/useTranslations';

export default function EmailValidation() {
  // eslint-disable-next-line
  const [error, setError] = useState('');
  const [inputValues, setInputValues] = useState(['', '', '', '']); // ["1", "2", "3", "4"]
  const [lastSubmittedCode, setLastSubmittedCode] = useState('');
  const [inputModified, setInputModified] = useState(false);
  const [isSubmitTriggered, setIsSubmitTriggered] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [emailVerificationDebounce, setEmailVerificationDebounce] = useState(false);
  const navigate = useNavigate();

  const [inputRefs] = useState([
    createRef<HTMLInputElement>(),
    createRef<HTMLInputElement>(),
    createRef<HTMLInputElement>(),
    createRef<HTMLInputElement>(),
  ]);

  const translations = useTranslations();

  const handleInputChange = (index: number, value: string) => {
    setError('');
    setInputModified(true);
    // check if value is a number or empty string
    if (/^\d+$/.test(value) || value === '') {
      const nextIndex = index + 1;
      if (value !== '') {
        // if value is not empty string, focus next input
        if (nextIndex < inputRefs.length && inputRefs[nextIndex].current) {
          inputRefs[nextIndex].current?.focus();
        } else {
          // remove focus from last input
          inputRefs[index].current?.blur();
        }
      }

      // set inputValues state to new value
      const newInputValues = [...inputValues];
      if (value.toString().length > 1) value = value.toString().slice(-1);
      newInputValues[index] = value;
      setInputValues(newInputValues);
    }
  };

  const sendEmail = useCallback(async () => {
    const email = window?.crm_user?.user.email;
    if (!email) return;

    setEmailVerificationDebounce(true);

    try {
      const response = await sendEmailVerification(email);
      if (response.status === 'ok') {
        setSnackbarMessage(translations?.snackBarMessages?.verificationEmail || 'Verification email has been sent');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(
          translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again.'
        );
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage(
        translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again.'
      );
      setSnackbarOpen(true);
      setError(translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again.');
    }
  }, [translations]);

  const navigateWithTimeout = useCallback(() => {
    setTimeout(() => {
      const redirectUrl = localStorage.getItem('redirect_url');
      localStorage.removeItem('redirect_url');
      const date = new Date().toISOString().split('.')[0] + '+01:00';
      if (window.crm_user) window.crm_user.user.email_validated_at = date;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        navigate(-1);
      }
    }, 3000);
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitTriggered) return;
    setIsSubmitTriggered(true);

    if (inputRefs.some((ref) => !ref.current?.value)) return;

    const digits = inputRefs.map((ref) => ref.current?.value || '').join('');

    // Check if digits form a four-digit number and it's different from the last submitted code
    if (digits.length === 4 && (digits !== lastSubmittedCode || inputModified)) {
      const email = window?.crm_user?.user.email;
      if (!email) return;

      try {
        const response = await verifyEmail(email, digits);
        if (response.status !== 'ok' || response.code !== 'success') {
          setError(translations?.snackBarMessages?.invalidCode || 'Invalid code');
          setSnackbarMessage(translations?.snackBarMessages?.invalidCode || 'Invalid code');
          setSnackbarOpen(true);
        } else {
          setSnackbarMessage(translations?.snackBarMessages?.emailVerified || 'Email verified');
          setSnackbarOpen(true);
          navigateWithTimeout();
        }
      } catch (error) {
        setSnackbarMessage(
          translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again.'
        );
        setSnackbarOpen(true);
        setError(translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again.');
      } finally {
        // Update lastSubmitted Code only if the request was sent
        setLastSubmittedCode(digits);
        setInputModified(false);
      }
    }

    setIsSubmitTriggered(false);
  }, [inputRefs, translations, navigateWithTimeout, lastSubmittedCode, inputModified, isSubmitTriggered]);

  useEffect(() => {
    if (inputValues.every((value) => value !== '')) handleSubmit();

    if (emailVerificationDebounce) {
      setTimeout(() => {
        setEmailVerificationDebounce(false);
      }, 30 * 1000);
    }
  }, [inputValues, emailVerificationDebounce, handleSubmit, setEmailVerificationDebounce]);

  useEffect(() => {
    sendEmail();
  }, [sendEmail]);

  return (
    <>
      <CustomSnackbar message={snackbarMessage} open={snackbarOpen} onClose={() => setSnackbarOpen(false)} />

      <div className="container">
        <div className="verify-email">
          <p>{translations?.authLabels?.digitCodeInstruction || 'We have sent you a verification code'}</p>

          <div className="verify-email__fields">
            {Array.from({ length: 4 }, (_, index) => (
              <div className="input-group" key={index}>
                <input
                  ref={inputRefs[index]}
                  id={`field-id-${Math.random()}`}
                  className="input"
                  type="number"
                  name={`evdigit${index + 1}`}
                  placeholder=""
                  maxLength={1}
                  value={inputValues[index]}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                />
              </div>
            ))}
          </div>

          <div>
            <button
              id="resend-email"
              className="button button--secondary button--small"
              onClick={sendEmail}
              disabled={emailVerificationDebounce}
            >
              {translations?.authLabels?.resendCodeString || "Didn't you get the code?"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
